import { findSumRuleEndingAt, type PageValidation } from "@patient-watch/forms/pageValidation";
import type { Control } from "react-hook-form";

import { useFormPageCopy } from "@/features/i18n/formPageCopy";
import { useGroupSum } from "../hooks/useGroupSum";
import { cn } from "@/lib/utils";

interface Props {
    validation: PageValidation | null;
    inputId: string;
    control: Control<Record<string, unknown>>;
}

/**
 * Running total under a group of inputs that must add up to a fixed number, updated as the patient
 * answers. Shown under the group's last input; renders nothing under any other input. Inputs of the
 * group on earlier pages count with their saved answers.
 */
const PageGroupTotal = ({ validation, inputId, control }: Props) => {
    const rule = findSumRuleEndingAt(validation, inputId);
    if (!rule) return null;
    return <GroupTotalLine inputIds={rule.input_ids} target={rule.total} control={control} />;
};

const GroupTotalLine = ({
    inputIds,
    target,
    control,
}: {
    inputIds: string[];
    target: number;
    control: Control<Record<string, unknown>>;
}) => {
    const copy = useFormPageCopy();
    const total = useGroupSum(inputIds, target, control);

    return (
        <output
            aria-live="polite"
            className={cn(
                "block mt-2 text-muted-foreground text-lg",
                total === target && "font-semibold text-foreground"
            )}
        >
            {copy.groupTotal(total, target)}
        </output>
    );
};

export default PageGroupTotal;
